// Schubladen- & Tür-Generator nach dem Korpus-Muster (cabinet.js):
// Maße orientieren sich am Korpus-Innenmaß, alle Teile sind an die linke
// Zarge (bzw. bei der Tür an sich selbst) geheftet, Positionen Ganzzahl-mm.
// Schublade steht zentriert um x/z = 0, Unterkante auf y = 0. Engine-frei.
import { newPart, generateClientId } from "./document"
import { DEFAULT_PART_TYPE } from "./part_types"

export const DRAWER_DEFAULTS = {
  width: 600, depth: 500, height: 150,
  thickness: 13, bottomThickness: 8, frontThickness: 19, gap: 3
}

export const DOOR_DEFAULTS = { width: 400, height: 700, thickness: 19, hinge: "left" }

// Positionen sind Ganzzahl-mm (DB-Konvention). Halbe Millimeter weg von Null
// runden, damit links/rechts spiegelsymmetrisch bleiben (siehe cabinet.js).
const roundPos = (v) => Math.sign(v) * Math.round(Math.abs(v))

// Generierte Ankerposition (Zarge links) für gegebene Parameter — Gegenstück
// zum Aufbau in buildDrawer, für den Positions-Delta beim Bearbeiten.
export function drawerAnchorPos({ width, gap, thickness, height }) {
  return { x: roundPos(-(width - 2 * gap - thickness) / 2), y: roundPos(height / 2), z: 0 }
}

// Generierte Ankerposition der Tür (die Tür ist ihr eigener Anker) — Gegenstück
// zum Aufbau in buildDoor, für den Positions-Delta beim Bearbeiten.
export function doorAnchorPos({ height }) {
  return { x: 0, y: roundPos(height / 2), z: 0 }
}

// Schublade für ein Korpus-Innenmaß `width` × `depth` (Zargenkasten läuft mit
// `gap` Luft je Seite): zwei Seitenzargen über die volle Tiefe, hintere Zarge
// dazwischen, Boden eingelassen (10 mm über Unterkante, vor der Hinterzarge),
// aufgesetzte Front `width` × (`height` + 30) mit 2 mm Luft vor den Zargen
// (v1 bewusst einfach: 15 mm Überstand oben und unten).
// `cabinetId` (optional) merkt am Anker, in welchem Korpus die Schublade sitzt
// (meta.belongs_to_cabinet) — die Montageanleitung sortiert die Gruppe dann
// direkt hinter diesen Korpus statt global ans Ende (editor/assembly.js).
export function buildDrawer(params = {}) {
  const { width, depth, height, thickness, bottomThickness, frontThickness, gap,
    materialId = null, groupId = generateClientId(), cabinetId = null } = { ...DRAWER_DEFAULTS, ...params }
  const s = thickness
  const boxWidth = width - 2 * gap        // Außenmaß des Zargenkastens
  const innerWidth = boxWidth - 2 * s     // lichtes Maß zwischen den Zargen
  if (innerWidth < 50 || depth < 100 || height < 30 || height < bottomThickness + 15) return null

  const parts = []
  const withIntegerPos = (attrs) => ({
    ...attrs,
    pos_x: roundPos(attrs.pos_x), pos_y: roundPos(attrs.pos_y), pos_z: roundPos(attrs.pos_z)
  })
  // Der Anker trägt die Gruppen-Id und die Parameter — Grundlage für
  // nachträgliches Bearbeiten (Dialog vorbefüllen, Teile ersetzen).
  const left = newPart(withIntegerPos({
    name: "Zarge links", material_id: materialId,
    length_mm: height, width_mm: depth, thickness_mm: s,
    rot_z: 90,
    pos_x: -(boxWidth - s) / 2, pos_y: height / 2, pos_z: 0,
    meta: {
      drawer_id: groupId, role: "zarge-links", part_type: DEFAULT_PART_TYPE,
      drawer: { width, depth, height, thickness, bottomThickness, frontThickness, gap },
      ...(cabinetId ? { belongs_to_cabinet: cabinetId } : {})
    }
  }))
  parts.push(left)

  // `role` = stabiler Schlüssel des Teils in der Gruppe; daran hängt beim
  // Bearbeiten die Übernahme der Handarbeit (generated_groups.js).
  // Zargen und Boden sind Korpusteile der Schublade, nur die Front ist Front.
  const attach = (role, attrs) => {
    parts.push(newPart(withIntegerPos({
      material_id: materialId, ...attrs,
      meta: {
        part_type: DEFAULT_PART_TYPE, ...(attrs.meta ?? {}),
        role, attached_to: left.client_id, drawer_id: groupId
      }
    })))
  }

  attach("zarge-rechts", {
    name: "Zarge rechts",
    length_mm: height, width_mm: depth, thickness_mm: s,
    rot_z: 90,
    pos_x: (boxWidth - s) / 2, pos_y: height / 2, pos_z: 0
  })
  attach("zarge-hinten", {
    name: "Zarge hinten",
    length_mm: innerWidth, width_mm: height, thickness_mm: s,
    rot_x: 90,
    pos_x: 0, pos_y: height / 2, pos_z: (depth - s) / 2
  })
  // Boden liegt zwischen den Zargen, Unterkante 10 mm über der Zargen-Unterkante,
  // in der Tiefe vor der Hinterzarge (Front bildet die vierte Wand)
  attach("schubboden", {
    name: "Boden",
    length_mm: innerWidth, width_mm: depth - s, thickness_mm: bottomThickness,
    pos_x: 0, pos_y: 10 + bottomThickness / 2, pos_z: -s / 2
  })
  attach("front", {
    name: "Front",
    length_mm: width, width_mm: height + 30, thickness_mm: frontThickness,
    rot_x: 90,
    pos_x: 0, pos_y: height / 2, pos_z: -(depth / 2 + 2 + frontThickness / 2),
    meta: { part_type: "front" }
  })

  return parts
}

// Tür/Front: eine stehende Frontplatte (Rückwand-Muster: rot_x 90, Höhe läuft
// über width_mm) mit zwei Topfband-Bohrungen Ø35 T12,5 an der Anschlagseite —
// je 100 mm von Ober-/Unterkante, 22,5 mm Randabstand. Die Töpfe sitzen auf
// der lokalen Oberseite (side "top"), die nach rot_x 90 nach hinten zeigt
// (Bandseite/Innenseite). Unterkante auf y = 0, zentriert um x/z = 0.
// `cabinetId` wie bei buildDrawer: Zuordnung der Tür zu ihrem Korpus.
export function buildDoor(params = {}) {
  const { width, height, thickness, hinge, materialId = null,
    groupId = generateClientId(), cabinetId = null } = { ...DOOR_DEFAULTS, ...params }
  if (width < 80 || height < 240 || thickness < 13) return null

  const sign = hinge === "right" ? 1 : -1
  const x = sign * (width / 2 - 22.5)
  const z = height / 2 - 100
  const bores = [
    { shape: "circle", diameter_mm: 35, depth_mm: 12.5, side: "top", x_mm: x, z_mm: -z, source: "hardware" },
    { shape: "circle", diameter_mm: 35, depth_mm: 12.5, side: "top", x_mm: x, z_mm: z, source: "hardware" }
  ]

  return newPart({
    name: "Tür", material_id: materialId,
    length_mm: width, width_mm: height, thickness_mm: thickness,
    rot_x: 90,
    pos_x: 0, pos_y: roundPos(height / 2), pos_z: 0,
    meta: {
      door_id: groupId, role: "tuer", part_type: "front",
      door: { width, height, thickness, hinge },
      ...(cabinetId ? { belongs_to_cabinet: cabinetId } : {}),
      cutouts: bores,
      hardware: [ { type: "topfband", item: "Topfband 35 mm", count: 2, source: "hardware" } ]
    }
  })
}
